import { useMemo } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import ServicesTypes from 'services/common/types.js';

const SportsMenu = MojitoModules.SportsMenu.module;
const RaceMeeting = MojitoModules.RaceMeeting;
const { EventFormatter } = MojitoPresentation.Utils;
const {
    FlexPane,
    DocumentHead,
    GlobalLinks,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    EmptyContent,
} = MojitoPresentation.Components;
import { useSelector } from 'react-redux';
const { selectMeetingsState } = MojitoServices.SportsContent.Meetings.selectors;
const { OFF, FINISHED } = MojitoServices.SportsContent.Events.types.RACE_STATUS;
const { ABANDONED } = MojitoServices.SportsContent.Events.types.RACE_STATE;
const { PENDING, UNKNOWN, AVAILABLE } = ServicesTypes.CONTENT_STATE;

const availableToBetOn = race => {
    const raceStatus = race.status;
    return raceStatus !== OFF && raceStatus !== FINISHED && raceStatus !== ABANDONED;
};

const RaceMeetingPageView = props => {
    const {
        sportId,
        meetingId,
        raceId,
        meetings,
        mojitoTools: { l10n, config, appContext },
    } = props;

    const meetingsState = useSelector(state => selectMeetingsState([meetingId], state));

    const documentHeadMetaFactory = useMemo(() => {
        const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;
        return new metaFactory(l10n);
    }, [config.documentHeadMetaFactory, l10n]);

    const renderDocumentHead = (sportId, meetings) => {
        const sportName = sportId && appContext.sportName(sportId);
        const meeting = meetings.length && meetings[0];

        if (!sportName || !meeting) {
            return null;
        }

        const meetingId = meeting.id;
        const meetingName = EventFormatter.formatEventName(meeting.name);

        const headData = {
            title: '$RACE_MEETING_PAGE.PAGE_TITLE',
            keywords: '$RACE_MEETING_PAGE.PAGE_KEYWORDS',
            description: '$RACE_MEETING_PAGE.PAGE_DESCRIPTION',
            canonical: '$RACE_MEETING_PAGE.PAGE_CANONICAL',
            localizedParams: new Map([
                ['sportName', sportName],
                ['meetingName', meetingName],
            ]),
            params: {
                sportId: sportId,
                eventId: meetingId,
            },
        };

        return <DocumentHead data={headData} metaFactory={documentHeadMetaFactory} />;
    };

    const isMeetingUnavailable = useMemo(
        () =>
            !meetingsState.some(
                state => state === AVAILABLE || state === PENDING || state === UNKNOWN
            ),
        [meetingsState]
    );

    const isMeetingDataLoaded = useMemo(
        () => !meetingsState.some(state => !state || state === PENDING || state === UNKNOWN),
        [meetingsState]
    );

    const raceEvent = useMemo(() => {
        const races = meetings.flatMap(meeting => meeting.races);

        return raceId
            ? races.find(race => race.id === raceId)
            : races.find(race => availableToBetOn(race));
    }, [raceId, meetings]);

    const noContent = (!raceEvent || isMeetingUnavailable) && isMeetingDataLoaded;

    return (
        <>
            {renderDocumentHead(sportId, meetings)}
            <PageSplitLayout>
                {/* Primary content */}
                <div className="ta-RaceMeetingPrimaryContent">
                    {!noContent && (
                        <RaceMeeting
                            sportId={sportId}
                            meetingId={meetingId}
                            raceId={raceId}
                            meetings={meetings}
                        />
                    )}
                    {noContent && <EmptyContent config={config.emptyContent} />}
                </div>
                {/* Secondary content */}
                <FlexPane
                    config={config.secondaryContentContainer}
                    class="ta-RaceMeetingSecondaryContent"
                >
                    <SportsMenu selectedSportId={sportId} />
                    <GlobalLinks />
                </FlexPane>
            </PageSplitLayout>
        </>
    );
};

export default RaceMeetingPageView;
